import { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import type {
  MissionAdminContractsForSettlementQuery,
  MissionAdminContractCampaignSettlementInfoQuery,
  MissionAdminContractCampaignSettlementInfoQueryVariables,
  MissionAdminCreateBrandSettlementMutation,
  MissionAdminCreateBrandSettlementMutationVariables,
} from "../../../__generated__/graphql";

const GET_CONTRACTS = gql`
  query MissionAdminContractsForSettlement {
    missionAdminContracts {
      id
      title
      brand {
        id
        name
      }
    }
  }
`;

const GET_CONTRACT_CAMPAIGN_SETTLEMENT_INFO = gql`
  query MissionAdminContractCampaignSettlementInfo($contractId: Int!) {
    missionAdminContractCampaignSettlementInfo(contractId: $contractId) {
      campaignId
      campaign {
        id
        productName
        channel
        productImageUrl
      }
      approvedCount
      paybackAmount
      operationFeeAmount
      totalExecutionAmount
      startedAt
      endedAt
    }
  }
`;

const CREATE_BRAND_SETTLEMENT = gql`
  mutation MissionAdminCreateBrandSettlement($input: CreateBrandSettlementInput!) {
    missionAdminCreateBrandSettlement(input: $input) {
      id
      settlementTitle
    }
  }
`;

interface OtherCost {
  id: string;
  name: string;
  amount: string;
}

interface TaxInvoice {
  businessName: string;
  businessNumber: string;
  representativeName: string;
  address: string;
  contactNumber: string;
  businessType: string;
}

const SettlementIssue = () => {
  const navigate = useNavigate();
  const [selectedContractId, setSelectedContractId] = useState<string>("");
  const [settlementTitle, setSettlementTitle] = useState<string>("");
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<Set<number>>(new Set());
  const [otherCosts, setOtherCosts] = useState<OtherCost[]>([]);
  const [taxInvoice, setTaxInvoice] = useState<TaxInvoice>({
    businessName: "",
    businessNumber: "",
    representativeName: "",
    address: "",
    contactNumber: "",
    businessType: "",
  });

  const { data: contractsData, loading: contractsLoading } = useQuery<MissionAdminContractsForSettlementQuery>(
    GET_CONTRACTS
  );
  const { data: settlementInfoData, loading: settlementInfoLoading } = useQuery<
    MissionAdminContractCampaignSettlementInfoQuery,
    MissionAdminContractCampaignSettlementInfoQueryVariables
  >(GET_CONTRACT_CAMPAIGN_SETTLEMENT_INFO, {
    variables: { contractId: parseInt(selectedContractId) },
    skip: !selectedContractId,
  });

  const [createSettlement, { loading: creating }] = useMutation<
    MissionAdminCreateBrandSettlementMutation,
    MissionAdminCreateBrandSettlementMutationVariables
  >(CREATE_BRAND_SETTLEMENT, {
    onCompleted: () => {
      alert("정산서가 발급되었습니다.");
      navigate("/ops/mission/settlement-issue");
      // 폼 초기화
      setSelectedContractId("");
      setSettlementTitle("");
      setSelectedCampaignIds(new Set());
      setOtherCosts([]);
      setTaxInvoice({
        businessName: "",
        businessNumber: "",
        representativeName: "",
        address: "",
        contactNumber: "",
        businessType: "",
      });
    },
    onError: (error) => {
      alert(`에러: ${error.message}`);
    },
  });

  const handleContractChange = (contractId: string) => {
    setSelectedContractId(contractId);
    setSelectedCampaignIds(new Set());
  };

  const toggleCampaignSelection = (campaignId: number) => {
    const newSet = new Set(selectedCampaignIds);
    if (newSet.has(campaignId)) {
      newSet.delete(campaignId);
    } else {
      newSet.add(campaignId);
    }
    setSelectedCampaignIds(newSet);
  };

  const addOtherCost = () => {
    setOtherCosts([
      ...otherCosts,
      {
        id: Date.now().toString(),
        name: "",
        amount: "",
      },
    ]);
  };

  const removeOtherCost = (id: string) => {
    setOtherCosts(otherCosts.filter((cost) => cost.id !== id));
  };

  const updateOtherCost = (id: string, field: "name" | "amount", value: string) => {
    setOtherCosts(
      otherCosts.map((cost) => (cost.id === id ? { ...cost, [field]: value } : cost))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedContractId) {
      alert("계약을 선택해주세요.");
      return;
    }

    if (!settlementTitle.trim()) {
      alert("정산서 제목을 입력해주세요.");
      return;
    }

    if (selectedCampaignIds.size === 0) {
      alert("최소 하나의 캠페인을 선택해주세요.");
      return;
    }

    if (
      !taxInvoice.businessName ||
      !taxInvoice.businessNumber ||
      !taxInvoice.representativeName
    ) {
      alert("세금계산서 필수 정보를 모두 입력해주세요.");
      return;
    }

    // 기타비용 검증
    const invalidOtherCosts = otherCosts.filter(
      (cost) => !cost.name.trim() || !cost.amount || parseFloat(cost.amount) <= 0
    );
    if (invalidOtherCosts.length > 0) {
      alert("기타비용 항목의 이름과 금액을 올바르게 입력해주세요.");
      return;
    }

    try {
      await createSettlement({
        variables: {
          input: {
            contractId: parseInt(selectedContractId),
            settlementTitle: settlementTitle.trim(),
            selectedCampaignIds: Array.from(selectedCampaignIds),
            otherCosts: otherCosts.map((cost) => ({
              name: cost.name.trim(),
              amount: parseFloat(cost.amount),
            })),
            taxInvoice: {
              businessName: taxInvoice.businessName.trim(),
              businessNumber: taxInvoice.businessNumber.trim(),
              representativeName: taxInvoice.representativeName.trim(),
              address: taxInvoice.address.trim() || null,
              contactNumber: taxInvoice.contactNumber.trim() || null,
              businessType: taxInvoice.businessType.trim() || null,
            },
          },
        },
      });
    } catch (error) {
      alert(`에러: ${error instanceof Error ? error.message : "알 수 없는 오류가 발생했습니다."}`);
    }
  };

  const formatChannel = (channel: string) => {
    const channelMap: Record<string, string> = {
      NAVER: "네이버",
      CARROT: "당근마켓",
      COUPANG: "쿠팡",
    };
    return channelMap[channel] || channel;
  };

  const formatNumber = (value: string | number) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? "0" : num.toLocaleString();
  };

  // 선택된 캠페인들의 합계 계산
  const selectedCampaigns =
    settlementInfoData?.missionAdminContractCampaignSettlementInfo?.filter((info) =>
      selectedCampaignIds.has(info.campaignId)
    ) || [];

  const totalPayback = selectedCampaigns.reduce(
    (sum: number, info) => sum + parseFloat(info.paybackAmount),
    0
  );
  const totalOperationFee = selectedCampaigns.reduce(
    (sum: number, info) => sum + parseFloat(info.operationFeeAmount),
    0
  );
  const totalExecutionCost = selectedCampaigns.reduce(
    (sum: number, info) => sum + parseFloat(info.totalExecutionAmount),
    0
  );
  const totalOtherCosts = otherCosts.reduce(
    (sum: number, cost: OtherCost) => sum + (parseFloat(cost.amount) || 0),
    0
  );
  const finalAmount = totalExecutionCost + totalOtherCosts;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">정산서 발급</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* 계약 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            계약 선택 <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedContractId}
            onChange={(e) => handleContractChange(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
            disabled={contractsLoading}
          >
            <option value="">계약을 선택하세요</option>
            {contractsData?.missionAdminContracts?.map((contract) => (
              <option key={contract.id} value={contract.id}>
                {contract.brand.name} - {contract.title}
              </option>
            ))}
          </select>
        </div>

        {/* 정산서 제목 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            정산서 제목 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={settlementTitle}
            onChange={(e) => setSettlementTitle(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            placeholder="예: 2025년 12월 1차 정산"
            required
          />
        </div>

        {/* 캠페인별 정보 */}
        {selectedContractId && (
          <div>
            <h2 className="text-lg font-semibold mb-4">캠페인별 정산 정보</h2>
            {settlementInfoLoading ? (
              <div className="text-center py-8 text-gray-500">로딩 중...</div>
            ) : settlementInfoData?.missionAdminContractCampaignSettlementInfo?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">캠페인이 없습니다.</div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          선택
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          캠페인
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          채널
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          승인된 건수
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          페이백 금액
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          운영수수료
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          정산금액
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {settlementInfoData?.missionAdminContractCampaignSettlementInfo?.map((info) => (
                          <tr
                            key={info.campaignId}
                            className={selectedCampaignIds.has(info.campaignId) ? "bg-blue-50" : ""}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedCampaignIds.has(info.campaignId)}
                                onChange={() => toggleCampaignSelection(info.campaignId)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {info.campaign.productImageUrl && (
                                  <img
                                    src={info.campaign.productImageUrl}
                                    alt={info.campaign.productName}
                                    className="w-10 h-10 object-cover rounded bg-gray-100"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                )}
                                <div className="text-sm font-medium text-gray-900">
                                  {info.campaign.productName}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-700">
                                {formatChannel(info.campaign.channel)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm text-gray-900">
                                {info.approvedCount.toLocaleString()}건
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm text-gray-900">
                                {formatNumber(info.paybackAmount)}원
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm text-gray-900">
                                {formatNumber(info.operationFeeAmount)}원
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm font-semibold text-gray-900">
                                {formatNumber(info.totalExecutionAmount)}원
                              </div>
                            </td>
                          </tr>
                        )
                      )}
                      {selectedCampaigns.length > 0 && (
                        <tr className="bg-gray-50 font-semibold">
                          <td colSpan={3} className="px-4 py-3 text-sm text-gray-700">
                            선택된 캠페인 합계
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                            {selectedCampaigns
                              .reduce((sum: number, info) => sum + info.approvedCount, 0)
                              .toLocaleString()}
                            건
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                            {formatNumber(totalPayback)}원
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                            {formatNumber(totalOperationFee)}원
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                            {formatNumber(totalExecutionCost)}원
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 기타비용 */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">기타비용</h2>
            <button
              type="button"
              onClick={addOtherCost}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              + 항목 추가
            </button>
          </div>
          {otherCosts.length === 0 ? (
            <p className="text-sm text-gray-500 italic">기타비용 항목이 없습니다.</p>
          ) : (
            <div className="space-y-3">
              {otherCosts.map((cost) => (
                <div
                  key={cost.id}
                  className="flex gap-4 items-end p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      항목명
                    </label>
                    <input
                      type="text"
                      value={cost.name}
                      onChange={(e) => updateOtherCost(cost.id, "name", e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="예: 추가 광고비"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">금액</label>
                    <input
                      type="number"
                      value={cost.amount}
                      onChange={(e) => updateOtherCost(cost.id, "amount", e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="0"
                      min="0"
                      step="1"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOtherCost(cost.id)}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    삭제
                  </button>
                </div>
              ))}
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="text-sm font-semibold text-gray-700 text-right">
                  기타비용 합계: {formatNumber(totalOtherCosts)}원
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 세금계산서 정보 */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold mb-4">세금계산서 정보</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                상호명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={taxInvoice.businessName}
                onChange={(e) =>
                  setTaxInvoice({ ...taxInvoice, businessName: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                사업자번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={taxInvoice.businessNumber}
                onChange={(e) =>
                  setTaxInvoice({ ...taxInvoice, businessNumber: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                대표자명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={taxInvoice.representativeName}
                onChange={(e) =>
                  setTaxInvoice({ ...taxInvoice, representativeName: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                연락처 (선택)
              </label>
              <input
                type="text"
                value={taxInvoice.contactNumber}
                onChange={(e) =>
                  setTaxInvoice({ ...taxInvoice, contactNumber: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                주소 (선택)
              </label>
              <input
                type="text"
                value={taxInvoice.address}
                onChange={(e) => setTaxInvoice({ ...taxInvoice, address: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">업종 (선택)</label>
              <input
                type="text"
                value={taxInvoice.businessType}
                onChange={(e) => setTaxInvoice({ ...taxInvoice, businessType: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>
        </div>

        {/* 최종 금액 요약 */}
        {selectedCampaigns.length > 0 && (
          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold mb-4">정산 금액 요약</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">집행 비용 (vat 별도):</span>
                <span className="font-semibold text-gray-900">
                  {formatNumber(totalExecutionCost)}원
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">기타 비용 (vat 별도):</span>
                <span className="font-semibold text-gray-900">
                  {formatNumber(totalOtherCosts)}원
                </span>
              </div>
              <div className="border-t border-gray-300 pt-3 flex justify-between">
                <span className="text-base font-semibold text-gray-900">최종 금액 (vat 별도):</span>
                <span className="text-xl font-bold text-gray-900">
                  {formatNumber(finalAmount)}원
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 버튼 */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate("/ops/mission/contracts/connect")}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={creating}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {creating ? "발급 중..." : "정산서 발급"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettlementIssue;
